import { PageLayout, TableNavigation, TablePaper } from 'components/common';
import { ListTable } from 'components/core';
import { useEffect } from 'react';
import { Link, Redirect, useLocation } from 'react-router-dom';
import {
  ORDER_TABLE_HEADERS,
  rowLayout as orderRowLayout,
} from 'utils/constants/Order';
import {
  rowLayout as shipmentRowLayout,
  SHIPMENT_TABLE_HEADERS,
} from 'utils/constants/Shipment';
import {
  useDetailsRedirect,
  useRelatedOrders,
  useRelatedShipments,
} from 'utils/hooks';

import { EntityEnum } from '@project44/entity-ui-utils';
import { Flex, Icon, Typography } from '@project44/manifest-ds';
import { Breadcrumbs } from '@project44/material-ui-deprecated';
import { Skeleton } from '@project44/shared-components';
import { useTranslation } from '@project44/translations-utils';
import { IOrder, ShipmentEntity } from '@project44-models/shipment-list';

import { getHeaders, useRelatedViewParam } from './utils';

function Crumbs({
  primary,
  secondary,
  search,
}: {
  primary: string;
  secondary: string;
  search: string;
}) {
  const { t } = useTranslation();
  return (
    <Breadcrumbs
      separator={<Icon icon="chevron_right" />}
      style={{ marginBottom: '16px' }}
    >
      <Link
        to={{ pathname: '/results', search }}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <TableNavigation title={primary} hideCount />
      </Link>
      <Typography variant="bodyBold" css={{ color: '$text-primary' }}>
        {t('core.relatedEntity', { entity: secondary })}
      </Typography>
    </Breadcrumbs>
  );
}

const Label = ({ label, isLoading }: { label: string; isLoading: boolean }) => (
  <Flex align="center" css={{ margin: '$large 0' }}>
    {isLoading ? (
      <Skeleton css={{ width: '112px', height: '24px' }} />
    ) : (
      <Typography variant="subtextBold">{label}</Typography>
    )}
  </Flex>
);

function RelatedView() {
  const { t } = useTranslation();
  const { listView, listViewId, isValid } = useRelatedViewParam();
  const handleRedirect = useDetailsRedirect();
  const location = useLocation<{ prevSearch: string }>();

  const relatedOrders = useRelatedOrders();
  const relatedShipments = useRelatedShipments();

  const isOrder = listView === 'order';
  useEffect(() => {
    if (isValid && listViewId) {
      const doFetch = isOrder
        ? relatedShipments.doFetch
        : relatedOrders.doFetch;
      doFetch(listViewId, { page: 1, size: 10 });
    }
  }, [
    isOrder,
    isValid,
    listViewId,
    relatedOrders.doFetch,
    relatedShipments.doFetch,
  ]);

  if (!isValid) {
    return <Redirect to="/" />;
  }

  const handleOrderClick = ({ orderId }: IOrder) => {
    handleRedirect(EntityEnum.order, orderId);
  };

  const handleShipmentClick = ({ shipmentId }: ShipmentEntity) => {
    handleRedirect(EntityEnum.shipment, shipmentId);
  };

  if (listView === 'shipment') {
    const { data, doFetch, isError, isLoading } = relatedOrders;
    const { results, paginationInfo } = data.relatedOrders;
    return (
      <PageLayout>
        <TablePaper>
          <Crumbs
            primary={t('core.shipments')}
            secondary={t('core.orders')}
            search={location.state?.prevSearch || `?listView=all-shipments`}
          />
          <ListTable
            title={t('core.shipments')}
            data={[data.shipment]}
            headers={getHeaders(
              SHIPMENT_TABLE_HEADERS.map(({ id, label, width }) => ({
                id,
                label,
                width,
              })),
            )}
            isError={isError}
            isLoading={isLoading}
            rowLayout={shipmentRowLayout}
            rowClick={handleShipmentClick}
            skeletonOptions={{ numOfRows: 1 }}
          />
          <Label
            isLoading={isLoading}
            label={t('core.relatedEntityCount', {
              entity: t('core.orders'),
              count: paginationInfo?.total,
            })}
          />
          <ListTable
            title={t('core.relatedEntity', { entity: t('core.orders') })}
            css={{ flex: 1 }}
            cssTable={{ flex: '1 1 0' }}
            data={results || []}
            headers={getHeaders(ORDER_TABLE_HEADERS)}
            isError={isError}
            isLoading={isLoading}
            rowClick={handleOrderClick}
            rowLayout={orderRowLayout}
            pagination={{
              ...paginationInfo,
              onPageChange: (page: number) => {
                if (listViewId) {
                  doFetch(listViewId, { page, size: 10 });
                }
              },
            }}
          />
        </TablePaper>
      </PageLayout>
    );
  } else {
    const { data, doFetch, isError, isLoading } = relatedShipments;
    const { results, paginationInfo } = data.relatedShipments;
    return (
      <PageLayout>
        <TablePaper>
          <Crumbs
            primary={t('core.orders')}
            secondary={t('core.shipments')}
            search={location.state?.prevSearch || `?listView=all-orders`}
          />
          <ListTable
            title={t('core.orders')}
            data={[data.order]}
            headers={getHeaders(
              ORDER_TABLE_HEADERS.map(({ id, label, width }) => ({
                id,
                label,
                width,
              })),
            )}
            isError={isError}
            isLoading={isLoading}
            rowClick={handleOrderClick}
            rowLayout={orderRowLayout}
            skeletonOptions={{ numOfRows: 1 }}
          />
          <Label
            isLoading={isLoading}
            label={t('core.relatedEntityCount', {
              entity: t('core.shipments'),
              count: paginationInfo?.total,
            })}
          />
          <ListTable
            title={t('core.relatedEntity', { entity: t('core.shipments') })}
            css={{ flex: 1 }}
            cssTable={{ flex: '1 1 0' }}
            data={results}
            headers={getHeaders(SHIPMENT_TABLE_HEADERS)}
            isError={isError}
            isLoading={isLoading}
            rowClick={handleShipmentClick}
            rowLayout={shipmentRowLayout}
            pagination={{
              ...paginationInfo,
              onPageChange: (page: number) => {
                if (listViewId) {
                  doFetch(listViewId, { page, size: 10 });
                }
              },
            }}
          />
        </TablePaper>
      </PageLayout>
    );
  }
}
export default RelatedView;
